import { normalizeCurrencyDisplay } from "./currencyDisplay.js";
import {
  normalizeAddressDisplayLabel,
  normalizeClientDisplayLabel,
  resolveOrderProductCodeDisplay,
} from "./displayLabelFormatters.js";
import {
  formatTonnage,
  isPlainObject,
  normalizeText,
  sortText,
  toNumber,
} from "./pageDataHelpers.js";
import { appendDispatchDateToUrl } from "./dispatchDateUrls.js";
import { resolveOptimizationStatusPresentation } from "./optimizationStatusPresentation.js";
import { isTruckCompatibilityActionable } from "./resultsFleetData.js";

export const ORDER_STATUS_META = {
  assigned_invalid_routing: {
    accentClassName: "bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200",
    bucket: "attention",
    label: "À corriger",
    summaryLabel: "À corriger",
  },
  assigned_valid: {
    accentClassName: "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200",
    bucket: "assigned",
    label: "Affectée",
    summaryLabel: "Affectées",
  },
  blocked_loading: {
    accentClassName: "bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200",
    bucket: "attention",
    label: "Blocage chargement",
    summaryLabel: "Bloquées",
  },
  unassigned: {
    accentClassName: "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200",
    bucket: "pending",
    label: "À planifier",
    summaryLabel: "À planifier",
  },
};

export const ORDER_STATUS_FILTERS = [
  { key: "all", label: "Toutes" },
  { key: "assigned", label: "Affectées" },
  { key: "pending", label: "À planifier" },
  { key: "attention", label: "Attention" },
];

const DATE_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

function formatDateLabel(value) {
  const normalizedValue = normalizeText(value);
  if (!normalizedValue) {
    return "Date non renseignée";
  }

  const parsedDate = new Date(normalizedValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return normalizedValue;
  }

  return DATE_FORMATTER.format(parsedDate);
}

function normalizeDisplayText(value) {
  return typeof value === "string" ? normalizeCurrencyDisplay(value.trim()) : "";
}

function resolveOrderAssignmentDetails(order) {
  const currentTruck = Array.isArray(order?.truck_compatibility)
    ? order.truck_compatibility.find((truck) => truck && truck.is_current_truck)
    : null;
  const planningNote = normalizeDisplayText(order?.planning_note);
  const parsedTripId = planningNote.match(/\b([A-Z0-9]+(?:-[A-Z0-9]+)*-TRIP-[A-Z0-9]+)\b/)?.[1] || "";
  const parsedTruckName = planningNote.match(/\bsur\s+([^,]+?)(?:,|$)/i)?.[1] || "";
  const truckId = normalizeDisplayText(order?.truck_id) || normalizeDisplayText(currentTruck?.truck_id);
  const truckName = normalizeDisplayText(order?.truck_name)
    || normalizeDisplayText(currentTruck?.truck_name)
    || normalizeDisplayText(parsedTruckName);

  return {
    tripId: normalizeDisplayText(order?.trip_id) || parsedTripId,
    truckId,
    truckName,
  };
}

function resolveOrderIssueLines(order, status) {
  const fallbackByStatus = {
    assigned_invalid_routing: "Affectation à corriger avant validation.",
    assigned_valid: "Tournée active dans le shell courant.",
    blocked_loading: "Chargement à corriger avant relance.",
    unassigned: "Commande en attente d'affectation.",
  };

  const fallbackLine = fallbackByStatus[status.code] || `${status.summaryLabel} dans le shell courant.`;
  const assignmentDetails = resolveOrderAssignmentDetails(order);
  const assignmentLines = [];

  if (assignmentDetails.tripId) {
    assignmentLines.push(`Tournée ${assignmentDetails.tripId}`);
  }

  if (assignmentDetails.truckName || assignmentDetails.truckId) {
    assignmentLines.push(`Camion ${assignmentDetails.truckName || assignmentDetails.truckId}`);
  }

  if (status.code === "assigned_valid") {
    return assignmentLines.length ? assignmentLines : [fallbackLine];
  }

  const resolvedText = normalizeDisplayText(order?.recommended_action)
    || normalizeDisplayText(order?.error_cause);
  if (!resolvedText) {
    return assignmentLines.length ? [...assignmentLines, fallbackLine] : [fallbackLine];
  }

  const lines = resolvedText
    .replace(/\s+/g, " ")
    .split(/\s*\|\s*|\s*;\s*|\s*\n+\s*/g)
    .map((item) => normalizeDisplayText(item))
    .filter(Boolean);

  const detailLines = lines.length ? lines : [fallbackLine];
  return assignmentLines.length ? [...assignmentLines, ...detailLines] : detailLines;
}

function resolveStatusMeta(order) {
  const statusCode = normalizeText(order?.execution_status_code) || "unassigned";

  if (ORDER_STATUS_META[statusCode]) {
    return {
      code: statusCode,
      ...ORDER_STATUS_META[statusCode],
    };
  }

  return {
    code: statusCode,
    ...ORDER_STATUS_META.unassigned,
  };
}

function normalizeOrderRow(order, index) {
  const safeOrder = isPlainObject(order) ? order : {};
  const status = resolveStatusMeta(safeOrder);
  const assignmentDetails = resolveOrderAssignmentDetails(safeOrder);
  const deliveryCount = Math.max(
    toNumber(
      safeOrder.delivery_count,
      Array.isArray(safeOrder.deliveries) ? safeOrder.deliveries.length : 0,
    ),
    0,
  );
  const compatibilityCount = Array.isArray(safeOrder.truck_compatibility)
    ? safeOrder.truck_compatibility.filter((truck) => isTruckCompatibilityActionable(truck)).length
    : 0;
  const orderId = normalizeText(safeOrder.order_id) || `ordre-${index + 1}`;
  const clientName = normalizeClientDisplayLabel(safeOrder.client_name) || "Client non renseigné";
  const loadingName = normalizeClientDisplayLabel(safeOrder.loading_client_name)
    || normalizeDisplayText(safeOrder.loading_client_code)
    || "Chargement à confirmer";
  const grainCode = resolveOrderProductCodeDisplay(safeOrder);
  const grainType = normalizeDisplayText(safeOrder.grain_type) || "Produit non renseigné";
  const workOrderNumber = normalizeText(safeOrder.work_order_number) || null;
  const issueLines = resolveOrderIssueLines(safeOrder, status);
  const statusLabel = status.label;

  return {
    address: normalizeAddressDisplayLabel(safeOrder.address) || "Adresse de livraison non renseignée",
    clientName,
    compatibilityCount,
    deliveryCount,
    deliveryDateLabel: formatDateLabel(safeOrder.delivery_date),
    grainCode,
    grainType,
    issueLines,
    key: `${orderId}-${index}`,
    loadingAddress: normalizeAddressDisplayLabel(safeOrder.loading_address) || "Adresse de chargement non renseignée",
    loadingName,
    orderId,
    quantityLabel: `${formatTonnage(Math.max(toNumber(safeOrder.quantity_tons, 0), 0))} t`,
    quantityTons: Math.max(toNumber(safeOrder.quantity_tons, 0), 0),
    searchText: [
      orderId,
      clientName,
      loadingName,
      grainType,
      safeOrder.client_code,
      safeOrder.loading_client_code,
      safeOrder.address,
      safeOrder.loading_address,
      statusLabel,
      assignmentDetails.tripId,
      assignmentDetails.truckId,
      assignmentDetails.truckName,
      issueLines.join(" "),
      workOrderNumber,
    ]
      .filter((value) => typeof value === "string" && value.trim())
      .join(" ")
      .toLowerCase(),
    status,
    statusLabel,
    workOrderNumber,
  };
}

export function matchesOrderStatusFilter(row, statusFilter) {
  if (statusFilter === "all") {
    return true;
  }

  return row.status.bucket === statusFilter;
}

export function buildGrainBreakdown(rows) {
  const countsByGrain = new Map();

  rows.forEach((row) => {
    const nextCount = (countsByGrain.get(row.grainType) || 0) + 1;
    countsByGrain.set(row.grainType, nextCount);
  });

  return Array.from(countsByGrain.entries())
    .map(([grainType, count]) => ({ count, grainType }))
    .sort((left, right) => right.count - left.count || sortText(left.grainType, right.grainType));
}

export function filterOrders(orders, { grainFilter = "all", searchQuery = "", statusFilter = "all" } = {}) {
  const normalizedSearchQuery = normalizeText(searchQuery).toLowerCase();

  return orders.filter((order) => {
    if (!matchesOrderStatusFilter(order, statusFilter)) {
      return false;
    }

    if (grainFilter !== "all" && order.grainType !== grainFilter) {
      return false;
    }

    if (normalizedSearchQuery && !order.searchText.includes(normalizedSearchQuery)) {
      return false;
    }

    return true;
  });
}

export function getNextDueOrder(orders) {
  return orders.find((order) => order.deliveryDateLabel !== "Date non renseignée") || null;
}

export function buildOrdersPageModel(initialReadModel = null) {
  const readModel = isPlainObject(initialReadModel) ? initialReadModel : {};
  const rawDashboard = isPlainObject(readModel.dashboard) ? readModel.dashboard : {};
  const rawOrders = Array.isArray(rawDashboard.missions) ? rawDashboard.missions : [];
  const orders = rawOrders.map((order, index) => normalizeOrderRow(order, index));
  const summary = {
    assigned: orders.filter((order) => order.status.bucket === "assigned").length,
    attention: orders.filter((order) => order.status.bucket === "attention").length,
    deliveries: orders.reduce((sum, order) => sum + order.deliveryCount, 0),
    pending: orders.filter((order) => order.status.bucket === "pending").length,
    tonnage: orders.reduce((sum, order) => sum + order.quantityTons, 0),
    total: orders.length,
  };
  const grainBreakdown = buildGrainBreakdown(orders);
  const latestOptimization = isPlainObject(readModel.latest_optimization) ? readModel.latest_optimization : {};
  const optimizationStatus = isPlainObject(rawDashboard.last_optimization) ? rawDashboard.last_optimization : null;

  return {
    grainBreakdown,
    grainOptions: [
      { key: "all", label: "Tous les produits" },
      ...grainBreakdown.map((item) => ({ key: item.grainType, label: item.grainType })),
    ],
    latestOptimization,
    optimizationStatus,
    optimizationStatusDisplay: resolveOptimizationStatusPresentation(optimizationStatus?.status, {
      hasOptimization: Boolean(optimizationStatus) || Boolean(latestOptimization.available),
    }),
    orders,
    rawDashboard,
    readModel,
    resultsHref: normalizeText(latestOptimization.href) || appendDispatchDateToUrl("/results"),
    statusFilters: ORDER_STATUS_FILTERS,
    summary,
  };
}